import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type OrderNomenclature from '@/Models/OrderNomenclature';
import Grid from '@mui/material/Grid';
import type { InvoiceModel } from '@/Models/InvoiceModel';
import type InvoiceRemark from '../../Models/InvoiceRemark';
import type { InvoiceRemarks } from '@/Models/InvoiceRemarks';
import type NomenclatureExclusion from '../../Models/NomenclatureExclusion';
import type NonAllowedOfficeHourSupplementRemark from '../../Models/NonAllowedOfficeHourSupplementRemark';
import type OnceADayGroupRemark from '../../Models/OnceADayGroupRemark';
import type OncePerIterationRemark from '../../Models/OncePerIterationRemark';
import type OncePerNrCalendarYearsGroupRemark from '../../Models/OncePerNrCalendarYearsGroupRemark';
import type OrderInvoiceTuple from '../../Models/OrderInvoiceTuple';
import type RecentOrder from '../../Models/RecentOrder';
import type TooMuchNonAmountSupplementRemark from '../../Models/TooMuchNonAmountSupplementRemark';
import Styles from './InvoiceRemarksGrid.module.scss';

function InvoiceRemarksGrid({ invoiceModel }: Readonly<{ invoiceModel: InvoiceModel }>): React.ReactElement {
    return (
        <Grid size={12} className={`${Styles.invoiceRemarks}`}>
            <InvoiceRemarksTable remarks={invoiceModel.Remarks} allOrderNomenclatures={invoiceModel.AllOrderNomenclatures} />
        </Grid>
    );
}

function InvoiceRemarksTable({ remarks, allOrderNomenclatures }: Readonly<{ remarks: InvoiceRemarks; allOrderNomenclatures: OrderNomenclature[] }>): React.ReactElement {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [dialogContent, setDialogContent] = useState<React.ReactElement[]>([]);
    const [dialogTitle, setDialogTitle] = useState<string>('');

    const allOrderNomens = allOrderNomenclatures?.map((item) => item.Number).filter((number) => number !== null) ?? [];

    remarks.Exclusions = remarks.Exclusions.filter((exclusion) => {
        return (
            allOrderNomens.includes(exclusion.Nomenclature1)
            || allOrderNomens.includes(exclusion.Nomenclature2)
            || (exclusion.GroupExclusion && allOrderNomens.includes(exclusion.Nomenclature1))
        );
    });

    const openDialog = (remarks: InvoiceRemark[], title: string) => () => {
        setDialogContent(remarks.map((remark) => {
            return remark.info(t);
        }));
        setDialogTitle(title);
        setOpen(true);
    };

    const closeDialog = (): void => {
        setOpen(false);
    };

    return (
        <div id="invoiceRemarks" className={Styles.invoiceRemarks}>
            <GetExclusions exclusions={remarks?.Exclusions} openDialog={openDialog} />
            <GetOnceADayRemarks onceADayRemarks={remarks?.OnceADayRemarks} openDialog={openDialog} />
            <GetOnceADayGroupRemarks onceADayGroupRemarks={remarks?.OnceADayGroupRemarks} openDialog={openDialog} />
            <GetOncePerNrCalendarYearsRemarks oncePerNrCalendarYearsRemarks={remarks?.OncePerNrCalendarYearsRemarks} openDialog={openDialog} />
            <GetOncePerNrCalendarYearsGroupRemarks oncePerNrCalendarYearsGroupRemarks={remarks?.OncePerNrCalendarYearsGroupRemarks} openDialog={openDialog} />
            <GetOncePerNrOfMonthsRemarks oncePerNrOfMonthsRemarks={remarks?.OncePerNrOfMonthsRemarks} openDialog={openDialog} />
            <GetHadForfait hadForfait={remarks?.HadForfait} />
            <GetHadConsultance hadConsultance={remarks?.HadConsultance} />
            <GetHadSupplement hadSupplement={remarks?.HadSupplement} />
            <GetHadNomenclatureWithAgeOutOfRange hadNomenclatureWithAgeOutOfRange={remarks?.HadNomenclatureWithAgeOutOfRange} />
            <GetHasHigherAmountSupplement hasHigherAmountSupplement={remarks?.HasHigherAmountSupplement} />
            <GetMultiplePercentageOrCalculated multiplePercentageOrCalculated={remarks?.MultiplePercentageOrCalculated} openDialog={openDialog} />
            <GetMultipleCustomAmountSupplements multipleCustomAmountSupplements={remarks?.MultipleCustomAmountSupplements} />
            <GetRecentOrders recentOrders={remarks?.RecentOrders} openDialog={openDialog} />
            <GetNonAllowedOfficeHourSupplementRemarks nonAllowedOfficeHourSupplementRemarks={remarks?.NonAllowedOfficeHourSupplementRemarks} openDialog={openDialog} />
            <GetHadReducedSupplement hadReducedSupplement={remarks?.HadReducedSupplement} />
            <GetOrphanedNomenclaturesRemarks orphanedNomenclatures={remarks?.OrphanedNomenclatureNumbers} />
            <Dialog open={open} onClose={closeDialog}>
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent>
                    <DialogContentText className={Styles.orderRemarkContent}>
                        {dialogContent}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog}>{t('Ok')}</Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

function GetExclusions({ exclusions, openDialog }: Readonly<{ exclusions: NomenclatureExclusion[]; openDialog: Function }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        exclusions && exclusions.length > 0
            ? <p className="combineExclusions orderRemark" onClick={openDialog(exclusions, t('NomenclatureCombinationsExclusions'))}>{t('NomenclatureCombinationsExclusions')}. {t('ClickForMoreInfo')}.</p>
            : <></>
    );
}

function GetOnceADayRemarks({ onceADayRemarks, openDialog }: Readonly<{ onceADayRemarks: OncePerIterationRemark[]; openDialog: Function }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        onceADayRemarks && onceADayRemarks.length > 0
            ? <p className="onceADayRemarks orderRemark" onClick={openDialog(onceADayRemarks, t('OnceADayRemarks'))}>{t('OnceADayRemarks')}. {t('ClickForMoreInfo')}.</p>
            : <></>
    );
}

function GetOnceADayGroupRemarks({ onceADayGroupRemarks, openDialog }: Readonly<{ onceADayGroupRemarks: OnceADayGroupRemark[]; openDialog: Function }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        onceADayGroupRemarks && onceADayGroupRemarks.length > 0
            ? <p className="onceADayGroupRemarks orderRemark" onClick={openDialog(onceADayGroupRemarks, t('OnceADayGroupRemarks'))}>{t('OnceADayGroupRemarks')}. {t('ClickForMoreInfo')}.</p>
            : <></>
    );
}

function GetOncePerNrCalendarYearsRemarks({ oncePerNrCalendarYearsRemarks, openDialog }: Readonly<{ oncePerNrCalendarYearsRemarks: OncePerIterationRemark[]; openDialog: Function }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        oncePerNrCalendarYearsRemarks && oncePerNrCalendarYearsRemarks.length > 0
            ? <p className="oncePerNrOfCalendarYearsRemark orderRemark" onClick={openDialog(oncePerNrCalendarYearsRemarks, t('OncePerNrOfCalendarYearsRemarks'))}>{t('OncePerNrOfCalendarYearsRemarks')}. {t('ClickForMoreInfo')}.</p>
            : <></>
    );
}

function GetOncePerNrCalendarYearsGroupRemarks({ oncePerNrCalendarYearsGroupRemarks, openDialog }: Readonly<{ oncePerNrCalendarYearsGroupRemarks: OncePerNrCalendarYearsGroupRemark[]; openDialog: Function }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        oncePerNrCalendarYearsGroupRemarks && oncePerNrCalendarYearsGroupRemarks.length > 0
            ? <p className="oncePerNrOfCalendarYearsGroupRemarks orderRemark" onClick={openDialog(oncePerNrCalendarYearsGroupRemarks, t('OncePerNrOfCalendarYearsGroupRemarks'))}>{t('OncePerNrOfCalendarYearsGroupRemarks')}. {t('ClickForMoreInfo')}.</p>
            : <></>
    );
}

function GetOncePerNrOfMonthsRemarks({ oncePerNrOfMonthsRemarks, openDialog }: Readonly<{ oncePerNrOfMonthsRemarks: OncePerIterationRemark[]; openDialog: Function }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        oncePerNrOfMonthsRemarks && oncePerNrOfMonthsRemarks.length > 0
            ? <p className="oncePerNrOfMonthsRemarks orderRemark" onClick={openDialog(oncePerNrOfMonthsRemarks, t('OncePerNrOfMonthsRemarks'))}>{t('OncePerNrOfMonthsRemarks')}. {t('ClickForMoreInfo')}.</p>
            : <></>
    );
}

function GetHadForfait({ hadForfait }: Readonly<{ hadForfait: OrderInvoiceTuple }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        hadForfait
            ? (
                    <p className="orderRemark">
                        {t('OrderRemarkForfait')}
                        <a href={'/Orders/Detail/' + hadForfait.OrderID} className="ajaxLinkNav">{' '}{t('Order')} #{hadForfait.OrderNumber}</a>
                        <span> </span>
                        <span>(</span>
                        <a href={'/Invoices/DetailConfirmed/' + hadForfait.InvoiceID} className="ajaxLinkNav">{t('Invoice')} #{hadForfait.InvoiceNumber}</a>
                        <span>.)</span>
                    </p>
                )
            : <></>
    );
}

function GetHadConsultance({ hadConsultance }: Readonly<{ hadConsultance: OrderInvoiceTuple }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        hadConsultance
            ? (
                    <p className="orderRemark">
                        {t('OrderRemarkConsultance')}
                        .
                        <a href={'/Orders/Detail/' + hadConsultance.OrderID} className="ajaxLinkNav">{' '}{t('Order')} #{hadConsultance.OrderNumber}</a>
                        <span> </span>
                        <span>(</span>
                        <a href={'/Invoices/DetailConfirmed/' + hadConsultance.InvoiceID} className="ajaxLinkNav">{t('Invoice')} #{hadConsultance.InvoiceNumber}</a>
                        <span>)</span>
                    </p>
                )
            : <></>
    );
}

function GetHadSupplement({ hadSupplement }: Readonly<{ hadSupplement: OrderInvoiceTuple }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        hadSupplement
            ? (
                    <p className="orderRemark">
                        {t('OrderRemarkSupplement')}
                        <a href={'/Orders/Detail/' + hadSupplement.OrderID} className="ajaxLinkNav">{' '}{t('Order')} #{hadSupplement.OrderNumber}</a>
                        <span> </span>
                        <span>(</span>
                        <a href={'/Invoices/DetailConfirmed/' + hadSupplement.InvoiceID} className="ajaxLinkNav">{t('Invoice')} #{hadSupplement.InvoiceNumber}</a>
                        <span>)</span>
                    </p>
                )
            : <></>
    );
}

function GetHadNomenclatureWithAgeOutOfRange({ hadNomenclatureWithAgeOutOfRange }: Readonly<{ hadNomenclatureWithAgeOutOfRange: boolean }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        hadNomenclatureWithAgeOutOfRange
            ? <p className="orderRemark">{t('NomenclatureWithAgeOutOfRange')}.</p>
            : <></>
    );
}

function GetHasHigherAmountSupplement({ hasHigherAmountSupplement }: Readonly<{ hasHigherAmountSupplement: OrderInvoiceTuple }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        hasHigherAmountSupplement
            ? (
                    <p className="orderRemark">
                        {t('OrderRemarkHigherSupplement')}
                        <a href={'/Orders/Detail/' + hasHigherAmountSupplement.OrderID} className="ajaxLinkNav">{' '}{t('Order')} #{hasHigherAmountSupplement.OrderNumber}</a>
                        <span> </span>
                        <span>(</span>
                        <a href={'/Invoices/DetailConfirmed/' + hasHigherAmountSupplement.InvoiceID} className="ajaxLinkNav">{t('Invoice')} #{hasHigherAmountSupplement.InvoiceNumber}</a>
                        <span>)</span>
                    </p>
                )
            : <></>
    );
}

function GetMultiplePercentageOrCalculated({ multiplePercentageOrCalculated, openDialog }: Readonly<{ multiplePercentageOrCalculated: TooMuchNonAmountSupplementRemark[]; openDialog: Function }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        multiplePercentageOrCalculated && multiplePercentageOrCalculated.length > 0
            ? <p className="multiplePercentageOrCalculated orderRemark" onClick={openDialog(multiplePercentageOrCalculated, t('MultiplePercentageOrCalculatedSupplements'))}>{t('MultiplePercentageOrCalculatedSupplements')}. {t('ClickForMoreInfo')}.</p>
            : <></>
    );
}

function GetMultipleCustomAmountSupplements({ multipleCustomAmountSupplements }: Readonly<{ multipleCustomAmountSupplements: boolean }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        multipleCustomAmountSupplements
            ? <p className="orderRemark">{t('MultipleCustomAmountSupplements')}.</p>
            : <></>
    );
}

function GetRecentOrders({ recentOrders, openDialog }: Readonly<{ recentOrders: RecentOrder[]; openDialog: Function }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        recentOrders && recentOrders.length > 0
            ? <p className="recentProcedure orderRemark" onClick={openDialog(recentOrders, t('AlreadyHadProcedureRecently'))}>{t('AlreadyHadProcedureRecently')}. {t('ClickForMoreInfo')}.</p>
            : <></>
    );
}

function GetNonAllowedOfficeHourSupplementRemarks({ nonAllowedOfficeHourSupplementRemarks, openDialog }: Readonly<{ nonAllowedOfficeHourSupplementRemarks: NonAllowedOfficeHourSupplementRemark[]; openDialog: Function }>): React.ReactElement {
    const { t } = useTranslation();
    return (
        nonAllowedOfficeHourSupplementRemarks && nonAllowedOfficeHourSupplementRemarks.length > 0
            ? <p className="nonAllowedOfficeHourSupplement orderRemark" onClick={openDialog(nonAllowedOfficeHourSupplementRemarks, t('SupplementNotAddedBecauseOfficeHours'))}>{t('SupplementNotAddedBecauseOfficeHours')}. {t('ClickForMoreInfo')}.</p>
            : <></>
    );
}

function GetHadReducedSupplement({ hadReducedSupplement }: Readonly<{ hadReducedSupplement: number }>): React.ReactElement {
    const { t } = useTranslation();
    return hadReducedSupplement && hadReducedSupplement > 0
        ? (
                <p className="orderRemark">
                    {t('MaximumAmountOfSupplements')}. (Maximum: &euro;{hadReducedSupplement})
                </p>
            )
        : (
                <></>
            );
}

function GetOrphanedNomenclaturesRemarks({ orphanedNomenclatures }: Readonly<{ orphanedNomenclatures: string[] }>): ReactElement {
    const { t } = useTranslation();
    if (orphanedNomenclatures.length == 0) {
        return <></>;
    }

    return (
        <p className="orderRemark">
            {t('NoValidParentNomenclaturesFound')} {orphanedNomenclatures.join(', ')}.
        </p>
    );
}

export default InvoiceRemarksGrid;
